import { ArrowRight, LoaderCircle, Mail, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { LoginBrandPanel } from '@/features/auth/components/LoginBrandPanel'
import { OtpInput } from '@/features/auth/components/OtpInput'
import { LOGIN_STEPS } from '@/features/auth/constants/auth.constants'
import { useAuth } from '@/features/auth/hooks/useAuth'
import {
  loginSchema,
  sendOtpSchema,
} from '@/features/auth/schemas/auth.schemas'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { toast } from '@/shared/lib/toast'

export function LoginPage() {
  const navigate = useNavigate()
  const { sendOtp, login, isLoading, clearError } = useAuth()

  const [step, setStep] = useState(LOGIN_STEPS.EMAIL)
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [resendCooldown, setResendCooldown] = useState(0)

  const startResendCooldown = () => {
    setResendCooldown(30)

    const timer = setInterval(() => {
      setResendCooldown((current) => {
        if (current <= 1) {
          clearInterval(timer)
          return 0
        }

        return current - 1
      })
    }, 1000)
  }

  const handleSendOtp = async (event) => {
    event.preventDefault()
    clearError()

    const parsed = sendOtpSchema.safeParse({ email })

    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message || 'Invalid email')
      return
    }

    try {
      await sendOtp(parsed.data.email)

      setEmail(parsed.data.email)
      setOtp('')
      setStep(LOGIN_STEPS.OTP)
      startResendCooldown()
    } catch {
      // Toast handled in store
    }
  }

  const handleVerifyOtp = async (event) => {
    event.preventDefault()
    clearError()

    const parsed = loginSchema.safeParse({
      email,
      otp,
    })

    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message || 'Invalid OTP')
      return
    }

    try {
      const { redirectTo } = await login(parsed.data)

      navigate(redirectTo, {
        replace: true,
      })
    } catch (error) {
      console.error(error)
    }
  }

  const handleResend = async () => {
    if (resendCooldown > 0 || isLoading) return

    clearError()

    try {
      await sendOtp(email)

      setOtp('')
      startResendCooldown()
    } catch {
      // Toast handled in store
    }
  }

  const handleChangeEmail = () => {
    clearError()
    setOtp('')
    setStep(LOGIN_STEPS.EMAIL)
  }

  return (
    <div className="grid h-dvh w-full overflow-hidden bg-[#f6f6fb] md:grid-cols-[1.08fr_0.92fr]">
      {/* Left Brand Section */}
      <LoginBrandPanel />

      {/* Right Login Section */}
      <main className="relative flex h-dvh min-h-0 items-center justify-center overflow-hidden px-4 py-6 sm:px-6 md:px-7 lg:px-10">
        {/* Soft background glow */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute right-[-15%] top-[-10%] h-[45%] w-[45%] rounded-full bg-indigo-100/40 blur-3xl" />

          <div className="absolute bottom-[-15%] left-[-10%] h-[40%] w-[45%] rounded-full bg-violet-100/30 blur-3xl" />
        </div>

        <div className="relative w-full max-w-[28rem]">
          <div className="rounded-[28px] border border-black/[0.04] bg-white/95 p-6 shadow-[0_25px_70px_rgba(15,23,42,0.09)] backdrop-blur-sm sm:p-8 md:p-9">
            {/* Secure badge */}
            <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3.5 py-1.5 text-xs font-semibold text-indigo-700">
              <span className="size-1.5 rounded-full bg-indigo-500" />
              Secure OTP sign-in
            </div>

            {/* Heading */}
            <h1 className="text-[clamp(1.8rem,3vw,2.2rem)] font-bold tracking-[-0.035em] text-slate-950">
              Sign in
            </h1>

            <p className="mt-2 max-w-[25rem] text-[15px] leading-6 text-slate-500 sm:text-base">
              {step === LOGIN_STEPS.EMAIL
                ? 'Your workspace is detected automatically from your registered email.'
                : 'Enter the 6-digit code we just sent to your inbox.'}
            </p>

            {step === LOGIN_STEPS.EMAIL ? (
              <form
                className="mt-7 space-y-4"
                onSubmit={handleSendOtp}
                noValidate
              >
                <div className="space-y-2">
                  <Label
                    htmlFor="email"
                    className="text-xs font-semibold uppercase tracking-wide text-slate-500"
                  >
                    Work email
                  </Label>

                  <div className="relative">
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@company.com"
                      value={email}
                      disabled={isLoading}
                      onChange={(event) => setEmail(event.target.value)}
                      className="h-13 rounded-2xl border-[1.5px] border-slate-200 bg-white pr-11 text-sm shadow-sm placeholder:text-slate-400 focus-visible:border-indigo-400 focus-visible:ring-indigo-100"
                    />

                    <Mail className="pointer-events-none absolute right-3.5 top-1/2 size-[18px] -translate-y-1/2 text-slate-400" />
                  </div>
                </div>

                <Button
                  type="submit"
                  variant="navy"
                  size="lg"
                  disabled={isLoading}
                  className="h-13 w-full rounded-2xl text-sm font-semibold shadow-sm"
                >
                  {isLoading ? (
                    <LoaderCircle className="size-5 animate-spin" />
                  ) : (
                    <>
                      Send OTP
                      <ArrowRight className="size-4" />
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <>
                {/* OTP information */}
                <div className="mt-6 flex items-start gap-3 rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-violet-50 p-4">
                  <div className="mt-0.5 grid size-9 shrink-0 place-items-center rounded-full bg-white text-indigo-600 shadow-sm">
                    <ShieldCheck className="size-[18px]" />
                  </div>

                  <div className="min-w-0">
                    <p className="break-all text-sm font-semibold text-indigo-950">
                      OTP sent to {email}
                    </p>

                    <button
                      type="button"
                      className="mt-1 text-xs font-semibold text-indigo-600 underline-offset-2 hover:underline disabled:opacity-60"
                      onClick={handleChangeEmail}
                      disabled={isLoading}
                    >
                      Change email
                    </button>
                  </div>
                </div>

                <form
                  className="mt-5 space-y-4"
                  onSubmit={handleVerifyOtp}
                  noValidate
                >
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      6-digit code
                    </Label>

                    <OtpInput
                      value={otp}
                      onChange={setOtp}
                      disabled={isLoading}
                      autoFocus
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={isLoading || otp.length !== 6}
                    className="h-13 w-full rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 text-sm font-semibold shadow-sm hover:from-indigo-600 hover:to-violet-700"
                  >
                    {isLoading ? (
                      <LoaderCircle className="size-5 animate-spin" />
                    ) : (
                      <>
                        Verify & continue
                        <ArrowRight className="size-4" />
                      </>
                    )}
                  </Button>
                </form>

                <p className="mt-5 text-center text-xs text-slate-500">
                  Didn&apos;t get the code?{' '}
                  <button
                    type="button"
                    className="font-semibold text-indigo-600 hover:underline disabled:text-slate-400"
                    onClick={handleResend}
                    disabled={isLoading || resendCooldown > 0}
                  >
                    {resendCooldown > 0
                      ? `Resend in ${resendCooldown}s`
                      : 'Resend'}
                  </button>
                </p>
              </>
            )}

            {/* Terms */}
            <p className="mt-7 text-center text-[11px] text-slate-400">
              By continuing you agree to the Superior CRM Terms.
            </p>
          </div>
        </div>
      </main>
    </div>
  )
}

export default LoginPage
